import { useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import {
  CREATE_CRISIS_MANAGEMENT_TEAM,
  DELETE_CRISIS_MANAGEMENT_TEAM,
  FIND_ALL_CRISIS_MANAGEMENT_TEAMS_BY_PLAN,
  UPDATE_CRISIS_MANAGEMENT_TEAM,
} from '../../../queries';
import TitleBar from '../../../../../components/TitleBar';
import { Footer } from '../../../../../components/common/Footer';
import { Loading } from '../../../../../components';
import useBCPMetaData from '../../../../../hooks/useBCPMetaData';
import { ColumnsType } from 'antd/es/table';
import { phoneFormatter, tableSortingMethods } from '../../../../../utils';
import TableV3, {
  ModifyVariablesArgs,
} from '../../../../../components/generic/Table/TableV3';
import QueryErrorBoundary from '../../../../../components/error-handling';
import { fields } from './formFields';
import { baseColumns } from './exportColumns';
import TitleUpdater from '../../../../../components/common/TitleUpdater';

export type CrisisManagementTeam = {
  backUpEmail: string;
  backUpFirstName: string;
  backUpLastName: string;
  backUpPhone: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  role: string;
  uuid: string;
};

export default function CrisisManagement() {
  const params = useParams();
  const { type, locationId } = params;

  const isLocationType = type === 'location';
  const backPath = `/bcp-landing/${type}/${locationId}`;

  const {
    planUUID,
    locationName,
    loading: loadingMetadata,
    error: errorMetadata,
  } = useBCPMetaData(locationId!, isLocationType);

  const { data, loading, error } = useQuery(
    FIND_ALL_CRISIS_MANAGEMENT_TEAMS_BY_PLAN,
    {
      variables: { uuid: planUUID },
      skip: !planUUID,
    },
  );

  const columns: ColumnsType<any> = [
    {
      title: 'Name',
      key: 'name',
      width: 150,
      align: 'left',
      sorter: (a, b) => tableSortingMethods.string(a.firstName, b.firstName),
      render: (_, { firstName, lastName }) =>
        firstName || lastName ? (
          <div className="custom-table--column custom-table--column-primary">
            {firstName} {lastName}
          </div>
        ) : (
          <span className="placeholder-text text-sm">Not Provided</span>
        ),
    },
    {
      title: 'Role/Title',
      key: 'role',
      align: 'center',
      width: 100,
      sorter: (a, b) => tableSortingMethods.string(a.role, b.role),
      render: (_, { role }) =>
        role ? (
          <span className="custom-table--column">{role}</span>
        ) : (
          <span className="placeholder-text text-sm">Not Provided</span>
        ),
    },
    {
      title: 'Contact',
      key: 'contact',
      align: 'left',
      width: 100,
      sorter: (a, b) => tableSortingMethods.string(a.email, b.email),
      render: (_, { email, phone }) =>
        email || phone ? (
          <div className="custom-table--column flex flex-col text-sm">
            <span>{email}</span>
            <span>{phoneFormatter(phone)} </span>
          </div>
        ) : (
          <span className="placeholder-text text-sm">Not Provided</span>
        ),
    },
    {
      title: 'Back-Up Name',
      key: 'backupName',
      align: 'left',
      width: 150,
      sorter: (a, b) =>
        tableSortingMethods.string(a.backUpFirstName, b.backUpFirstName),
      render: (_, { backUpFirstName, backUpLastName }) =>
        backUpFirstName || backUpLastName ? (
          <span className="custom-table--column">
            {backUpFirstName} {backUpLastName}
          </span>
        ) : (
          <span className="placeholder-text text-sm">Not Provided</span>
        ),
    },

    {
      title: 'Back-Up Contact',
      key: 'backupContact',
      align: 'left',
      width: 100,
      sorter: (a, b) =>
        tableSortingMethods.string(a.backUpEmail, b.backUpEmail),
      render: (_, { backUpEmail, backUpPhone }) =>
        backUpEmail || backUpPhone ? (
          <div className="flex items-center custom-table--column text-sm">
            <div className="flex flex-col items-start">
              <span>{backUpEmail}</span>
              <span>{phoneFormatter(backUpPhone)} </span>
            </div>
          </div>
        ) : (
          <span className="placeholder-text text-sm">Not Provided</span>
        ),
    },
  ];

  const [createGQL] = useMutation(CREATE_CRISIS_MANAGEMENT_TEAM, {
    refetchQueries: ['FindAllCrisisManagementTeamsByPlan'],
  });

  const [updateGQL] = useMutation(UPDATE_CRISIS_MANAGEMENT_TEAM, {
    refetchQueries: ['FindAllCrisisManagementTeamsByPlan'],
  });

  const [deleteGQL] = useMutation(DELETE_CRISIS_MANAGEMENT_TEAM, {
    refetchQueries: ['FindAllCrisisManagementTeamsByPlan'],
  });

  const addVariables = ({ values, type }: ModifyVariablesArgs) => {
    if (type === 'create') {
      return {
        ...values,
        plan: { uuid: planUUID },
      };
    }
    return {
      ...values,
    };
  };

  return (
    <QueryErrorBoundary error={errorMetadata}>
      <TitleUpdater title={`Crisis Management Team`} />
      <TitleBar
        title="BCP Hub"
        subtitle="Crisis Management Team"
        brief={
          isLocationType
            ? `The Location Crisis Management Team (LCMT) is responsible for critical decision making 
              during a prolonged business disruption at the location level. It is also responsible for liaising with the Corporate 
              Crisis Management Team (CCMT). At the location level, identify the 
              individuals who will be the key location points of contact during a disruption:`
            : `The Corporate Crisis Management Team (CCMT) is responsible for critical decision making
              during a prolonged business disruption. It is also responsible for liaising with the Location
              Crisis Management Team (LCMT) of the affected location. At the corporate level, identify the
              individuals who will be the key corporate points of contact during a disruption:`
        }
      />
      <QueryErrorBoundary error={error}>
        {!loading ? (
          <TableV3
            title={`${
              isLocationType ? locationName : 'Corporate'
            } Crisis Management Team Members`}
            columns={columns}
            data={data?.findAll || []}
            loading={loadingMetadata || loading}
            error={error}
            editable
            formFields={fields}
            createGQL={createGQL}
            updateGQL={updateGQL}
            deleteGQL={deleteGQL}
            modifyVariables={addVariables}
            exportExcelColumns={baseColumns}
          />
        ) : (
          <Loading />
        )}
      </QueryErrorBoundary>
      <Footer backPath={backPath} />
    </QueryErrorBoundary>
  );
}
